import React from "react";
import SortBy from "../../../UI/Icons/SortBy";
import Warning from "../../../UI/Icons/Warning";
import HoverTooltip from "../../../UI/Icons/HoverTooltip";
import { Link } from "react-router";

interface SortControlsProps {
  /**
   * The current field by which the list is sorted.
   */
  sortBy: string;
  /**
   * The current sorting order (ascending or descending).
   */
  sortOrder: string;
  /**
   *
   * @param type - The type of sorting to be applied (e.g., "created_at").
   * @returns
   */
  onSortChange: (type: string) => void;

  page_type: "pending" | "accepted" | "history" | "jobs";
}

/**
 * A React functional component that renders sorting controls for a list of items.
 * It allows users to sort by "Requested Timeframe" or "Posted" date, and visually
 * indicates the current sorting field and order.
 *
 * @component
 * @param {SortControlsProps} props - The props for the SortControls component.
 * @param {"timeframe" | "created_at"} props.sortBy - The current field by which the list is sorted.
 * @param {"asc" | "desc"} props.sortOrder - The current sorting order (ascending or descending).
 * @param {(field: "timeframe" | "created_at") => void} props.onSortChange - Callback function to handle sorting field changes.
 *
 * @returns {JSX.Element} The rendered SortControls component.
 */
const SortControls: React.FC<SortControlsProps> = ({
  sortBy,
  sortOrder,
  onSortChange,
  page_type,
}) => {
  return (
    <>
      <div className="d-none d-lg-flex gap-20 align-items-center px-15px py-10px">
        <div
          className="min-w-210px w-210px d-flex gap-5 align-items-center cursor-pointer"
          onClick={() => onSortChange("timeframe")}
        >
          <div className="fs-13 fw-700 text-black">Requested Timeframe</div>
          <SortBy
            fill={sortBy === "timeframe" ? "#167E76" : "#E0E0E0"}
            sort_order={sortBy === "timeframe" ? sortOrder : ""}
          />
        </div>

        <div className="flex-grow-1">
          <div className="d-flex gap-20 align-items-center">
            <div className="min-w-50px"></div>
            <div
              className="flex-grow-1 d-flex gap-5 align-items-center cursor-pointer"
              onClick={() => onSortChange("position_name")}
            >
              <div className="fs-13 fw-700 text-black">Position Type</div>
              <SortBy
                fill={sortBy === "position_name" ? "#167E76" : "#E0E0E0"}
                sort_order={sortBy === "position_name" ? sortOrder : ""}
              />
            </div>
          </div>
        </div>

        <div
          className="min-w-85px w-85px d-flex gap-5 align-items-center cursor-pointer"
          onClick={() => onSortChange("created_at")}
        >
          <div className="fs-13 fw-700 text-black">Posted</div>
          <SortBy
            fill={sortBy === "created_at" ? "#167E76" : "#E0E0E0"}
            sort_order={sortBy === "created_at" ? sortOrder : ""}
          />
        </div>

        {page_type === "pending" && (
          <div
            className="min-w-85px w-85px d-flex gap-5 align-items-center cursor-pointer"
            onClick={() => onSortChange("applied_at")}
          >
            <div className="fs-13 fw-700 text-black">Applied</div>
            <SortBy
              fill={sortBy === "applied_at" ? "#167E76" : "#E0E0E0"}
              sort_order={sortBy === "applied_at" ? sortOrder : ""}
            />
          </div>
        )}

        {page_type === "accepted" && (
          <>
            <div
              className="min-w-85px w-85px d-flex gap-5 align-items-center cursor-pointer"
              onClick={() => onSortChange("accepted_at")}
            >
              <div className="fs-13 fw-700 text-black">Accepted</div>
              <SortBy
                fill={sortBy === "accepted_at" ? "#167E76" : "#E0E0E0"}
                sort_order={sortBy === "accepted_at" ? sortOrder : ""}
              />
            </div>
          </>
        )}

        <div className="min-w-100px w-100px gap-10">
          <div className="fs-13 fw-700 text-black">Pay Rate&nbsp;
            <HoverTooltip
                position="bottom"
                content={
                  <div className="fs-13 fw-400 text-black">
                    Find out more about{" "}
                    <Link
                      className="fw-700 text-black"
                      to="#"
                      target="_blank"
                      onClick={(e) => e.preventDefault()}
                    >
                      Position Types & Pay Rates.
                    </Link>{" "}
                    Daily allowances may be added in addition to help cover the anticipated costs of travel.
                  </div>
                }
                className={"w-317px min-w-317px mobile-w-100 position-absolute text-black px-15px py-8px radius-20px bg-teal-pale text-center"}
                type={"job"}
              >
                <Warning width="20" height="20" color="#0A625B" />
              </HoverTooltip>
            </div>
          </div>
        <div
          className={`${
            page_type === "accepted" ? "min-w-290px" : "min-w-290px"
          } `}
        ></div>
      </div>
      <div className="border-bottom border-grey-light"></div>
    </>
  );
};

export default SortControls;
